import type { ComponentType, Dispatch, SetStateAction, SVGProps } from "react";
import { FiRepeat } from "react-icons/fi";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import Spinner from "../../../shared/components/Spinner";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { formatDateTimeLabel } from "../../../shared/utils/dateTime";
import {
  CategoryChangeRequest,
  CategoryChangeRequestStatus,
  userCategoryLabel,
} from "../../auth/shared/types/auth";

type AdminCategoryRequestsPanelProps = {
  adminNotes: Record<number, string>;
  loading: boolean;
  onLoad: () => Promise<void>;
  onResolve: (requestId: number, action: "approve" | "reject") => Promise<void>;
  requests: CategoryChangeRequest[];
  resolveLoadingId: number | null;
  setAdminNotes: Dispatch<SetStateAction<Record<number, string>>>;
  setStatus: Dispatch<SetStateAction<CategoryChangeRequestStatus | "all">>;
  status: CategoryChangeRequestStatus | "all";
};

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const CategoryRequestIcon = FiRepeat as unknown as IconLike;

function categoryRequestStatusLabel(status: CategoryChangeRequestStatus) {
  const labels = siteTexts.admin.categoryRequestsTab.singularStatus;
  if (status === "approved") return labels.approved;
  if (status === "rejected") return labels.rejected;
  return labels.pending;
}

export default function AdminCategoryRequestsPanel({
  adminNotes,
  loading,
  onLoad,
  onResolve,
  requests,
  resolveLoadingId,
  setAdminNotes,
  setStatus,
  status,
}: AdminCategoryRequestsPanelProps) {
  const t = siteTexts.admin;

  return (
    <div className="admin-content">
      <AdminSection className="admin-users-dashboard">
        <div className="admin-users-hero">
          <div className="admin-users-hero-copy">
            <span className="admin-users-hero-icon" aria-hidden="true">
              <CategoryRequestIcon />
            </span>
            <div>
              <h2>{t.categoryRequestsTab.title}</h2>
              <p>Analise pedidos de mudança de categoria dos atletas.</p>
            </div>
          </div>
          <Button
            type="button"
            variant="pill"
            onClick={onLoad}
            disabled={loading}
          >
            {t.categoryRequestsTab.refresh}
          </Button>
        </div>

        <div className="status-filter-buttons">
          {(["pending", "approved", "rejected", "all"] as const).map(
            (nextStatus) => (
              <Button
                key={nextStatus}
                type="button"
                variant="pill"
                className={status === nextStatus ? "active" : ""}
                onClick={() => setStatus(nextStatus)}
              >
                {nextStatus === "pending"
                  ? t.categoryRequestsTab.filters.pending
                  : nextStatus === "approved"
                    ? t.categoryRequestsTab.filters.approved
                    : nextStatus === "rejected"
                      ? t.categoryRequestsTab.filters.rejected
                      : t.categoryRequestsTab.filters.all}
              </Button>
            ),
          )}
        </div>

        {loading && (
          <p className="status-message">
            <Spinner label={t.categoryRequestsTab.loading} />
          </p>
        )}

        {!loading && requests.length === 0 && (
          <EmptyState
            className="admin-empty-state"
            icon={<span className="material-symbols-outlined">inbox</span>}
            title={t.categoryRequestsTab.empty}
          />
        )}

        <div className="admin-list admin-category-requests-list">
          {requests.map((row) => {
            const resolveDisabled = resolveLoadingId === row.id;
            return (
              <article
                key={`category-request-${row.id}`}
                className="admin-card admin-category-request-card"
              >
                <header className="admin-category-request-card-header">
                  <div>
                    <p className="admin-category-request-label">
                      {t.misc.athlete}
                    </p>
                    <h3>
                      {row.userName || t.categoryRequestsTab.playerFallback}
                    </h3>
                    <span>
                      {row.userEmail || t.categoryRequestsTab.noEmail}
                    </span>
                  </div>
                  <span
                    className={`admin-category-request-status admin-category-request-status--${row.status}`}
                  >
                    {categoryRequestStatusLabel(row.status)}
                  </span>
                </header>

                <div className="admin-category-request-grid">
                  <div>
                    <span>{t.misc.currentCategory}</span>
                    <strong>{userCategoryLabel(row.currentCategory)}</strong>
                  </div>
                  <div>
                    <span>{t.misc.requestedCategory}</span>
                    <strong>{userCategoryLabel(row.requestedCategory)}</strong>
                  </div>
                  <div>
                    <span>{t.misc.requestDate}</span>
                    <strong>{formatDateTimeLabel(row.createdAt)}</strong>
                  </div>
                  <div>
                    <span>{t.categoryRequestsTab.status}</span>
                    <strong>{categoryRequestStatusLabel(row.status)}</strong>
                  </div>
                </div>

                <div className="admin-category-request-reason">
                  <span>{t.categoryRequestsTab.reason}</span>
                  <p>{row.reason || t.misc.noReason}</p>
                </div>

                {row.status === "pending" ? (
                  <>
                    <label className="field">
                      <span>{t.misc.rejectionNote}</span>
                      <textarea
                        value={adminNotes[row.id] || ""}
                        onChange={(event) =>
                          setAdminNotes((prev) => ({
                            ...prev,
                            [row.id]: event.target.value,
                          }))
                        }
                        rows={3}
                        maxLength={1000}
                        placeholder={
                          t.categoryRequestsTab.decisionNotePlaceholder
                        }
                      />
                    </label>
                    <div className="admin-actions-row">
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => onResolve(row.id, "approve")}
                        disabled={resolveDisabled}
                      >
                        {resolveDisabled
                          ? t.categoryRequestsTab.processing
                          : t.categoryRequestsTab.approve}
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => onResolve(row.id, "reject")}
                        disabled={resolveDisabled}
                      >
                        {resolveDisabled
                          ? t.categoryRequestsTab.processing
                          : t.categoryRequestsTab.reject}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    {row.adminNote && (
                      <p className="status-message admin-category-request-admin-note">
                        {t.categoryRequestsTab.decisionNotePrefix}{" "}
                        <span className="muted">{row.adminNote}</span>
                      </p>
                    )}
                    {row.resolvedByName && (
                      <p className="status-message admin-category-request-admin-note">
                        {t.categoryRequestsTab.processedBy} {row.resolvedByName}
                        {row.resolvedAt
                          ? ` ${t.categoryRequestsTab.processedAtPrefix} ${formatDateTimeLabel(row.resolvedAt)}`
                          : ""}
                      </p>
                    )}
                  </>
                )}
              </article>
            );
          })}
        </div>
      </AdminSection>
    </div>
  );
}
